<script setup lang="ts">
import PerformanceSparkline from '@/components/eos/charts/PerformanceSparkline.vue'
import PerformanceTrackerTable from '@/components/eos/table/PerformanceTable.vue'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useDARTPerformanceTrackerService } from '@/composables/eos/da-rt-performance-tracker'
import type { DAPerformanceTrackLine } from '@/types/eos/DARTPerformanceTracker'
import { formatCurrency } from '@/util/formatters'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Refresh } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { sum } from 'lodash'
import { DateTime } from 'luxon'
import { computed, ref } from 'vue'

enum SummaryMode {
  Daily,
  Monthly,
}

const selectedSite = ref<ISiteIdentifier>()
const selectedMode = ref<SummaryMode>(SummaryMode.Daily)

const selectedTimezone = ref<string>('EST')
const selectedDate = ref<Date>(DateTime.now().minus({ days: 1 }).toJSDate()) // Default to yesterday

/** Flag to call the server to force-refresh the query */
const forceRefresh = ref(false)

// Query bindings

const { performanceTrackQuery, performanceTrackMonthlySummaryQuery } = useDARTPerformanceTrackerService()

const {
  data: dataDaily,
  error: errorDaily,
  isRefetching: isRefetchingDaily,
  refetch: refetchDaily,
  isPending: isPendingDaily,
  isLoading: isLoadingDaily,
} = performanceTrackQuery(selectedSite, selectedDate, selectedTimezone, forceRefresh)

const {
  data: dataMonthly,
  error: errorMonthly,
  isRefetching: isRefetchingMonthly,
  refetch: refetchMonthly,
  isPending: isPendingMonthly,
} = performanceTrackMonthlySummaryQuery(selectedSite, selectedDate, selectedTimezone)

// Data points and totals

/** Returns earnings if there's an affecting_dispatch, else 0 */
function getRowRtEarnings(row: DAPerformanceTrackLine): number {
  return (row.affecting_dispatch && row.rt_earnings) ?? 0
}

/** Returns earnings if there's an affecting_dispatch, else 0 */
function getRowDaEarnings(row: DAPerformanceTrackLine): number {
  return (row.affecting_dispatch && row.da_earnings) ?? 0
}

// RT

const rtDataPoints = computed(() => {
  if (selectedMode.value === SummaryMode.Daily && dataDaily.value) return dataDaily.value.map(getRowRtEarnings)
  return []
})

const rtTotalEarnings = computed(() => {
  if (selectedMode.value === SummaryMode.Monthly && dataMonthly.value) return dataMonthly.value.rt_earnings_sum
  if (selectedMode.value === SummaryMode.Daily && rtDataPoints.value) return sum(rtDataPoints.value)
  return 0
})

// DA

const daDataPoints = computed(() => {
  if (selectedMode.value === SummaryMode.Daily && dataDaily.value) return dataDaily.value.map(getRowDaEarnings)
  return []
})

const daTotalEarnings = computed(() => {
  if (selectedMode.value === SummaryMode.Monthly && dataMonthly.value) return dataMonthly.value.da_earnings_sum
  if (selectedMode.value === SummaryMode.Daily && daDataPoints.value) return sum(daDataPoints.value)
  return 0
})

// SUM

const sumDataPoints = computed(() => {
  if (selectedMode.value === SummaryMode.Daily && dataDaily.value)
    return dataDaily.value.map((record) => getRowRtEarnings(record) + getRowDaEarnings(record))
  return []
})

const sumTotalEarnings = computed(() => {
  if (selectedMode.value === SummaryMode.Monthly && dataMonthly.value)
    return dataMonthly.value.rt_earnings_sum + dataMonthly.value.da_earnings_sum
  if (selectedMode.value === SummaryMode.Daily) return rtTotalEarnings.value + daTotalEarnings.value
  return 0
})

// Curtailed hours

const totalCurtailedHours = computed(() => {
  if (selectedMode.value === SummaryMode.Monthly && dataMonthly.value) return dataMonthly.value.total_curtailed_hours
  if (selectedMode.value === SummaryMode.Daily && dataDaily.value)
    return dataDaily.value.filter((record) => !!record.affecting_dispatch).length
  return 0
})

// Query state

const dataReady = computed(() => {
  if (selectedMode.value === SummaryMode.Daily) {
    return !!dataDaily.value
  } else {
    return !!dataMonthly.value
  }
})

const queryPending = computed(() => {
  if (selectedMode.value === SummaryMode.Daily) {
    return isPendingDaily.value
  } else {
    return isPendingMonthly.value
  }
})

const emptyText = computed(() => {
  if ((!!selectedSite.value && queryPending.value) || isRefetchingDaily.value || isRefetchingMonthly.value) {
    return 'Loading...'
  } else if (!selectedSite.value) {
    return 'Please select a site.'
  }
  return 'No data.'
})

const shouldDisplayError = computed(() => {
  if (selectedMode.value === SummaryMode.Daily) {
    return errorDaily.value && !isRefetchingDaily.value
  } else {
    return errorMonthly.value && !isRefetchingMonthly.value
  }
})

const error = computed(() => {
  if (selectedMode.value === SummaryMode.Daily) {
    return errorDaily.value
  } else {
    return errorMonthly.value
  }
})

// Handlers

/**
 * Refetch table query with forceRefresh enabled
 */
const handleForceRefresh = async () => {
  forceRefresh.value = true
  try {
    await refetchDaily()
  } catch {
    // Error handling is done by Vue Query
  } finally {
    forceRefresh.value = false
  }
}

/** Util that disables current date and dates in the future */
const isDateDisabled = (date: Date) => {
  return DateTime.fromJSDate(date) >= DateTime.now().startOf('day')
}

const isInputDisabled = computed(() => isLoadingDaily.value || isRefetchingDaily.value)
</script>

<template>
  <ViewLayout>
    <template v-if="!error" #controls>
      <el-button
        :icon="Refresh"
        type="primary"
        class="actions-button"
        :loading="isRefetchingDaily || isRefetchingMonthly"
        :disabled="!selectedSite || !selectedDate || isRefetchingDaily"
        @click="handleForceRefresh"
        >Force Refresh</el-button
      >
    </template>
    <rl-error-result v-if="shouldDisplayError" :error="error">
      <el-button @click="() => (selectedMode === SummaryMode.Daily ? refetchDaily() : refetchMonthly())">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex gap-4">
        <rc-site-select v-model="selectedSite" class="w-[180px]" :disabled="isInputDisabled" />
        <el-date-picker
          v-model="selectedDate"
          class="!w-[140px]"
          type="date"
          placeholder="Select date"
          :disabled="isInputDisabled"
          :disabled-date="isDateDisabled"
          :clearable="false"
        />
      </div>
      <el-card class="flex flex-col gap-6">
        <!-- Card top -->
        <div class="flex items-center justify-between">
          <span class="uppercase text-xs">Earnings</span>
          <el-tabs v-model="selectedMode" class="max-w-[240px]">
            <el-tab-pane label="Daily" :name="SummaryMode.Daily" />
            <el-tab-pane label="Monthly" :name="SummaryMode.Monthly" />
          </el-tabs>
        </div>

        <!-- Numbers and sparklines -->
        <div class="flex items-center justify-between gap-8">
          <!-- RT -->
          <div class="min-w-60 flex flex-col justify-between gap-1 pr-20">
            <span class="text-text-tertiary text-sm">Real Time (RT)</span>
            <div class="flex gap-4">
              <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
                >$
                <span class="text-text-primary font-semibold">{{
                  dataReady ? formatCurrency(rtTotalEarnings, 2, false) : '-'
                }}</span></span
              >
              <PerformanceSparkline :data-points="rtDataPoints" color="#0BCD92" />
            </div>
          </div>

          <!-- DA -->
          <div class="min-w-60 flex flex-col justify-between gap-1 pr-20">
            <span class="text-text-tertiary text-sm">Day Ahead (DA)</span>
            <div class="flex gap-4">
              <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
                >$
                <span class="text-text-primary font-semibold">{{
                  dataReady ? formatCurrency(daTotalEarnings, 2, false) : '-'
                }}</span></span
              >
              <PerformanceSparkline :data-points="daDataPoints" color="#FF770A" />
            </div>
          </div>

          <!-- Total -->
          <div class="min-w-60 flex flex-col justify-between gap-1 pr-20">
            <span class="text-text-tertiary text-sm">Total</span>
            <div class="flex gap-4">
              <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
                >$
                <span class="text-text-primary font-semibold">{{
                  dataReady ? formatCurrency(sumTotalEarnings, 2, false) : '-'
                }}</span></span
              >
              <PerformanceSparkline :data-points="sumDataPoints" color="#FFD21B" />
            </div>
          </div>

          <!-- Curtailed hours -->
          <div class="min-w-60 flex flex-col justify-between gap-1 pr-20">
            <span class="text-text-tertiary text-sm">Curtailed Hours</span>
            <div class="flex gap-4">
              <span class="text-text-primary text-2xl w-max whitespace-nowrap font-semibold">
                {{ dataReady ? totalCurtailedHours : '-' }}
              </span>
            </div>
          </div>
        </div>
      </el-card>

      <!-- Actual table -->
      <PerformanceTrackerTable
        v-if="dataReady"
        v-loading="isRefetchingDaily"
        mode="DA"
        :data="dataDaily || []"
        :monthly-summary="dataMonthly"
        :show-monthly-summary="true"
        :timezone="selectedTimezone"
      />

      <div v-else class="h-[50vh] flex items-center justify-center text-text-tertiary">{{ emptyText }}</div>

      <!-- Spacer -->
      <div class="w-full h-8" />
    </template>
  </ViewLayout>
</template>

<style lang="scss" scoped>
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-tabs;
  @include overrides.wide-el-tabs;
  @include overrides.black-bg-select;
  @include overrides.black-bg-input;
}
</style>
